import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Book, Upload, Sliders, Download, Users, Link2, BarChart3 } from "lucide-react";

interface DocumentationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentationModal({ open, onOpenChange }: DocumentationModalProps) {
  const sections = [
    {
      icon: Upload,
      title: "Uploading Invoices",
      content:
        "Upload PDF invoices individually or in batch via the Upload button. DiscoRun AI uses OCR to extract supplier name, email, payment amount, and due date. If any fields can't be parsed, you'll be prompted to fill them in manually. Supported formats: PDF.",
    },
    {
      icon: Sliders,
      title: "Setting Your APR Rate",
      content:
        "The APR (Annual Percentage Rate) determines the discount offered to suppliers for early payment. Use the slider for values up to 40% or type a custom value for higher rates. The optimal range is 8–20%. Higher APR means bigger discounts for suppliers but also bigger savings for you.",
    },
    {
      icon: Download,
      title: "ACH File Generation",
      content:
        'Once suppliers accept early payment offers, their invoices appear as "Accepted - Due." Click Download ACH to generate a NACHA-formatted .txt file for your bank. This automatically marks the invoices as "Paid." You can process multiple invoices in a single batch.',
    },
    {
      icon: BarChart3,
      title: "Budget Management",
      content:
        "Set your early payment budget to control how much capital is allocated for discounted payments. When the budget is exceeded, new offers are held until budget is increased. Use Quick Add buttons to top up your budget. Budget alerts are automatically emailed to your notification address.",
    },
    {
      icon: Users,
      title: "User Management",
      content:
        "Invite team members with three role levels: Admin (full platform control), Manager (can process invoices and manage settings), and Viewer (read-only access to dashboards and reports). Manage roles and remove users from the Users panel.",
    },
    {
      icon: Link2,
      title: "Integrations",
      content:
        "Connect your ERP and AP systems (SAP, QuickBooks, NetSuite, etc.) to sync invoice data automatically. Click on any integration to enter credentials. Active integrations sync in real-time; inactive ones can be connected at any time.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Book className="w-5 h-5 text-primary" />
            Documentation
          </DialogTitle>
          <DialogDescription>
            Learn how to use DiscoRun AI to optimize your early payment discounts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <h4 className="font-semibold text-foreground mb-2">Getting Started</h4>
            <p className="text-sm text-muted-foreground">
              DiscoRun AI helps you save money by offering suppliers early payment discounts. The
              workflow is simple: set your APR rate → upload invoices → suppliers receive offers →
              accepted invoices are paid early via ACH. Your dashboard tracks all metrics, savings,
              and supplier engagement in real-time.
            </p>
          </div>

          {sections.map((section) => (
            <div key={section.title} className="space-y-2">
              <h4 className="font-semibold text-foreground flex items-center gap-2">
                <section.icon className="w-4 h-4 text-primary" />
                {section.title}
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                {section.content}
              </p>
            </div>
          ))}

          <div className="border-t border-border pt-4 mt-4">
            <p className="text-center text-sm text-muted-foreground">
              Need more help? Contact us at{" "}
              <a href="mailto:support@discorun.ai" className="text-primary hover:underline">
                support@discorun.ai
              </a>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
